import axios from 'axios';
import { useState, useRef, useEffect, Fragment } from 'react';
import { useRouter } from 'next/router';
import { YMaps, Map, ZoomControl, GeolocationControl, Button, withYMaps, ObjectManager, Polygon, Placemark } from 'react-yandex-maps';
import { isMobile } from 'react-device-detect';
import { JSONToQuery } from '../../helpers';
import cleanCoords from '@turf/clean-coords';
import unkinkPolygon from '@turf/unkink-polygon';
import combine from '@turf/combine';
import buffer from '@turf/buffer';
import { useLocationState } from '../../context/location';
import { useObjectsProvider } from '../../context/objects';
import { useIsMount } from '../../hooks/useIsMount';
import ObjectsMapCatalog from './ObjectsMapCatalog';
import ObjectsMapCatalogMobile from './ObjectsMapCatalogMobile';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import css from '../../styles/modules/objects/ObjectsMap.module.scss';

import Filter from '../../public/images/filter.svg';
import LoupePlus from '../../public/images/loupeplus.svg';
import LoupeMinus from '../../public/images/loupeminus.svg';
import Geo from '../../public/images/geo.svg';
import FullScreen from '../../public/images/fullscreen.svg';

const TemplateProvider = withYMaps(({ children, template }) => children({ template }), true, ['templateLayoutFactory']);
const polygonDefaults = {
  strokeColor: '#52bf41',
  strokeWidth: 3.5,
  fillColor: '#52bf41',
  fillOpacity: 0.2,
  interactivityModel: 'default#transparent'
};

const isAnyFilters = (s) => {
  const arrFilters = [
    'priceFrom',
    'priceTo',
    'rooms',
    'studio',
    'squareFrom',
    'squareTo',
    'mikroraion',
    'squareAreaFrom',
    'squareAreaTo',
    'commercialType',
    'squareLivingFrom',
    'squareLivingTo',
    'squareKitchenFrom',
    'squareKitchenTo',
    'floorFrom',
    'floorTo',
    'floorsFrom',
    'floorsTo',
    'ceiling',
    'balcony',
    'renovation',
    'bathroom',
    'buildingType',
    'stock',
    'lotType',
    'subCategory',
    'entrance',
    'commercialBuildingType'
  ];
  let filtersTotal = 0;

  for (let i = 0; i < arrFilters.length; i++)
    if (
      s[arrFilters[i]] &&
      (s[arrFilters[i]].length || s[arrFilters[i]] > 0)
    )
      filtersTotal++;

  return filtersTotal > 0;
};

const ObjectsMap = () => {
  /** STATES */
  const router = useRouter();
  const isMount = useIsMount();
  const locationState = useLocationState();
  const { width } = useWindowDimensions();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [ymaps, setYmaps] = useState();
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(Boolean(objectsState.map.searchZone.length));
  const [drawPolygon, setDrawPolygon] = useState(objectsState.map.searchZone);
  const [objectManagerFeatures, setObjectManagerFeatures] = useState([]);
  const [clickedActivePoint, setClickedActivePoint] = useState();
  const [zoomLevel, setZoomLevel] = useState(locationState.zoom);

  /** REFS */
  const mapId = useRef();
  const drawButtonId = useRef();

  /** FUNCTIONS */
  const drawCustomPolygon = (y) => {
    const rect = y.container.getParentElement().getBoundingClientRect();
    const canvas = document.createElement('canvas');
    const ctx2d = canvas.getContext('2d');

    let isDrawing = false;
    let coordinates = [];
    let offsets = [];

    /** Setting canvas same size as map element. */
    canvas.style.width = `${rect.width} px`;
    canvas.style.height = `${rect.height} px`;
    canvas.style.zIndex = '50000';
    canvas.style.position = 'fixed';
    canvas.style.right = 0;
    canvas.style.bottom = 0;
    canvas.style.left = 0;
    canvas.style.top = 0;
    canvas.style.opacity = 1;
    canvas.width = rect.width;
    canvas.height = rect.height;

    /** Setting styles. */
    ctx2d.strokeStyle = polygonDefaults.strokeColor;
    ctx2d.lineWidth = polygonDefaults.strokeWidth;

    y.panes.get('overlaps').getElement().append(canvas);

    const onDown = (e) => {
      isDrawing = true;

      coordinates.push([e.pageX, e.pageY]);
      offsets.push([e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top]);
    };
    const onMove = (e) => {
      if (!isDrawing) return;

      const last = offsets[offsets.length - 1];

      ctx2d.beginPath();
      ctx2d.moveTo(last[0], last[1]);
      ctx2d.lineTo(e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top);
      ctx2d.stroke();

      coordinates.push([e.pageX, e.pageY]);
      offsets.push([e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top]);
    };
    const onUp = () => {
      canvas.parentNode.removeChild(canvas);
      isDrawing = false;

      const projection = y.options.get('projection');
      coordinates = coordinates.map((x) =>
        projection.fromGlobalPixels(
          y.converter.pageToGlobal([x[0], x[1]]),
          y.getZoom()
        )
      );

      return coordinates;
    };

    canvas.onmousedown = (e) => onDown(e);
    canvas.ontouchstart = (e) => {
      e.preventDefault();
      onDown(e.touches[0]);
    };

    canvas.onmousemove = (e) => onMove(e);
    canvas.ontouchmove = (e) => {
      e.preventDefault();
      onMove(e.touches[0]);
    };

    return new Promise((resolve) => {
      /** When mouse up saving coordinates and hiding canvas. */
      canvas.onmouseup = (e) => {
        if (coordinates.length > 10)
          resolve(onUp(e));

        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        offsets = [];
        coordinates = [];
        isDrawing = false;
      };
      canvas.ontouchend = (e) => {
        if (coordinates.length > 10)
          resolve(onUp(e));

        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        offsets = [];
        coordinates = [];
        isDrawing = false;
      };
    });
  };
  const handleCatalogBackClick = () => {
    let newLink = '/' + router.query.region + '/objects';

    switch (objectsState.filter.type) {
      default:
      case 'продажа':
        newLink += '/buy';
        break;

      case 'аренда':
        newLink += '/rent';
        break;
    }
    switch (objectsState.filter.category) {
      default:
      case 'квартира':
        newLink += '/flats';
        break;

      case 'комната':
        newLink += '/rooms';
        break;

      case 'дом':
        newLink += '/houses';
        break;

      case 'участок':
        newLink += '/sites';
        break;

      case 'коммерция':
        newLink += '/commercial';
        break;
    }
    if (!objectsState.filter.studio && objectsState.filter.rooms.length === 1) {
      switch (objectsState.filter.rooms[0]) {
        case '1':
          newLink += '/1k';
          break;

        case '2':
          newLink += '/2k';
          break;

        case '3':
          newLink += '/3k';
          break;

        case '4':
          newLink += '/4k';
          break;

        default:
          break;
      }
    } else if (objectsState.filter.studio && !objectsState.filter.rooms.length) {
      newLink += '/studio';
    }

    setObjectsState({
      type: 'SET_FILTER',
      payload: {
        ...objectsState.filter,
        ads: []
      }
    });

    router.push(
      `${newLink}${JSONToQuery(
        { ...objectsState.filter, ...objectsState.pagination, ...objectsState.sorting },
        [
          'type',
          'category',
          'length',
          'ads',
          objectsState.filter.studio && !objectsState.filter.rooms.length && 'studio',
          !objectsState.filter.studio && objectsState.filter.rooms.length === 1 && 'rooms'
        ],
        ['page=1', 'sort=last-update.date'],
        true
      )}`
    );
  };
  const handleMapLoad = (e) => {
    setYmaps(e);
    setIsMapReady(true);
    setIsLoading(false);

    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        bbox: mapId.current.getBounds()
      }
    });
  };
  const handleOnBoundsChange = (e) => {
    setZoomLevel(e.originalEvent.newZoom);
    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        bbox: mapId.current.getBounds(),
        zoomLevel: e.originalEvent.newZoom
      }
    });

    router.replace(
      `/${router.query.region}/objects/map${JSONToQuery(
        {
          ...objectsState.filter,
          ...objectsState.map
        },
        ['bbox', 'zoomLevel', 'ads'],
        [],
        true
      )}`
    );
  };
  const handleClickOnPoint = (e) => {
    const currentZoom = mapId.current.getZoom();
    const target = e.originalEvent.currentTarget.clusters.getById(e.get('objectId')) || e.originalEvent.currentTarget.objects.getById(e.get('objectId'));

    if (currentZoom < 14.5) {
      mapId.current.setCenter(target.type === 'Cluster' ? ymaps.util.bounds.getCenter(target.bbox) : target.geometry.coordinates, 15, { duration: 300 });
    } else {
      setClickedActivePoint(target);
      setObjectsState({
        type: 'SET_FILTER',
        payload: { ...objectsState.filter, ads: target.properties.clusterObjects || [target.id] }
      });
      setObjectsState({
        type: 'SET_STATE',
        payload: { ...objectsState.state, isMapCatalogVisible: true }
      });

      router.replace(
        `/${router.query.region}/objects/map${JSONToQuery(
          {
            ...objectsState.filter,
            ...objectsState.map,
            ads: target.properties.clusterObjects || [target.id]
          },
          ['bbox', 'zoomLevel', 'ads'],
          [],
          true
        )}`
      );
    }
  };
  const handleDrawButtonClick = () => {
    setIsDrawing(!isDrawing);
    setDrawPolygon([]);

    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        searchZone: []
      }
    });
  };

  /** HOOKS */
  useEffect(() => {
    if (isMount) return;

    if (isDrawing) {
      /** If want to draw. */
      drawCustomPolygon(mapId.current).then((coordinates) => {
        const cuttedCoordinates = coordinates.filter((_, i) => i % 3 === 0);
        const unkinkPolygons = unkinkPolygon(
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [cuttedCoordinates]
            }
          },
          { tolerance: 1e-7 }
        );
        const cleanedPolygon = cleanCoords(buffer(combine(unkinkPolygons), 1e-12).features[0]).geometry.coordinates;
        const sizedPolygon = cleanedPolygon[0].map((v) => [Number(v[0].toFixed(5)), Number(v[1].toFixed(5))]);

        const newMapState = {
          ...objectsState.map,
          searchZone: sizedPolygon
        };

        setDrawPolygon(sizedPolygon);
        setObjectsState({
          type: 'SET_MAP',
          payload: newMapState
        });

        router.replace(
          `/${router.query.region}/objects/map${JSONToQuery(
            { ...objectsState.filter, ...newMapState },
            ['bbox', 'zoomLevel', 'ads'],
            [],
            true
          )}`
        );
      });
    } else {
      setDrawPolygon([]);
      setObjectsState({
        type: 'SET_MAP',
        payload: {
          ...objectsState.map,
          searchZone: []
        }
      });

      router.replace(
        `/${router.query.region}/objects/map${JSONToQuery(
          {
            ...objectsState.filter,
            ...objectsState.map,
            searchZone: []
          },
          ['bbox', 'zoomLevel', 'ads'],
          [],
          true
        )}`
      );
    }
  }, [isDrawing]);
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!objectsState.filter.ads.length)
      setClickedActivePoint();

    const fetchData = async() => {
      try {
        const request = await axios.post(process.env.NEXT_PUBLIC_API + '/objects/map', {
          ...objectsState.filter,
          bbox: objectsState.map.bbox,
          zoom: objectsState.map.zoomLevel,
          searchZone: objectsState.map.searchZone,
          location: locationState.region,
          ads: []
        }, { signal });

        if (!request.data)
          throw new Error('Something wrong with getting server data! [ObjectsMap -> fetchData()]');

        setObjectManagerFeatures(request.data.features);
      } catch (error) {}
    };
    fetchData();
  }, [objectsState.filter, objectsState.map.bbox, objectsState.map.searchZone]);
  useEffect(() => {
    if (isMount || drawPolygon.length) return;

    if (mapId.current)
      mapId.current.setCenter(locationState.coordinates, locationState.zoom);
  }, [locationState]);

  /** TEMPLATE */
  return (
    <section className={['block', 'clear', css.map].join(' ')}>
      {isLoading && !isMapReady && (
        <div className={css.loading}>
          <img src="/images/loading_map.svg" alt="" />
          <div>Загрузка картографических данных...</div>
        </div>
      )}
      <YMaps query={{ apikey: process.env.NEXT_PUBLIC_YANDEX_KEY }}>
        <TemplateProvider>
          {() => (
            <Map
              modules={['util.bounds']}
              style={{ width: '100%', height: '100%' }}
              defaultState={{
                center: locationState.coordinates,
                zoom: locationState.zoom,
                controls: [],
                behaviors: ['drag', 'dblClickZoom', 'multiTouch', 'scrollZoom'],
                margin: [0, 0, 0, width > 992 && objectsState.state.isMapCatalogVisible ? 420 : 0]
              }}
              defaultOptions={{
                maxZoom: 20,
                maxAnimationZoomDifference: Infinity,
                autoFitToViewport: 'always',
                yandexMapDisablePoiInteractivity: true,
                suppressObsoleteBrowserNotifier: true,
                suppressMapOpenBlock: true
              }}
              onLoad={handleMapLoad}
              onBoundschange={handleOnBoundsChange}
              instanceRef={(ref) => {
                if (ref)
                  mapId.current = ref;
              }}
            >
              {isMapReady && (
                <Fragment>
                  {clickedActivePoint && (
                    <Placemark
                      geometry={clickedActivePoint.geometry.coordinates}
                      options={clickedActivePoint.type === 'Cluster' ? {
                        iconLayout: ymaps.templateLayoutFactory.createClass(`
                          <div class="point cluster active">
                            <div class="information">от ${clickedActivePoint.priceText}</div>
                            ${clickedActivePoint.properties.clusterInfo}
                          </div>`),
                          iconShape: zoomLevel > 14.5 ? {
                          type: 'Rectangle',
                          coordinates: [[-38, -26], [62, 22]]
                        } : {
                          type: 'Circle',
                          coordinates: [12, 12],
                          radius: 11
                        },
                        iconOffset: [-12, -12],
                        zIndex: 10
                      } : {
                        iconLayout: ymaps.templateLayoutFactory.createClass(
                          `<div class="point active">
                            <div class="information">${clickedActivePoint.priceText}</div>
                          </div>`
                        ),
                        iconShape: zoomLevel > 14.5 ? {
                          type: 'Rectangle',
                          coordinates: [[-28, -26], [52, 14]]
                        } : {
                          type: 'Circle',
                          coordinates: [8, 8],
                          radius: 7
                        },
                        iconOffset: [-6, -6],
                        zIndex: 10
                      }}
                    />
                  )}
                  {Boolean(drawPolygon.length) && (
                    <Polygon
                      geometry={[drawPolygon]}
                      options={polygonDefaults}
                    />
                  )}
                  <ObjectManager
                    objects={{
                      iconLayout: ymaps.templateLayoutFactory.createClass(
                        zoomLevel > 14.5 ? `
                          <div class="point">
                            {% if price > 0 %}
                            <div class="information">{{priceText}}</div>
                            {% endif %}
                          </div>
                        ` : `
                          <div class="point"></div>
                        `
                      ),
                      iconShape: zoomLevel > 14.5 ? {
                        type: 'Rectangle',
                        coordinates: [[-28, -26], [52, 14]]
                      } : {
                        type: 'Circle',
                        coordinates: [8, 8],
                        radius: 7
                      },
                      iconOffset: [-6, -6],
                      hasBalloon: false,
                      zIndex: 6
                    }}
                    clusters={{
                      clusterIconLayout: ymaps.templateLayoutFactory.createClass(
                        zoomLevel > 14.5 ? `
                          <div class="point cluster">
                            {% if price > 0 %}
                            <div class="information">от {{priceText}}</div>
                            {% endif %}
                            {% if properties.clusterInfo < 100 %}
                            {{properties.clusterInfo}}
                            {% else %}
                            99
                            {% endif %}
                          </div>
                        ` : `
                          <div class="point cluster">
                            {% if properties.clusterInfo < 100 %}
                            {{properties.clusterInfo}}
                            {% else %}
                            99
                            {% endif %}
                          </div>
                        `
                      ),
                      clusterIconShape: zoomLevel > 14.5 ? {
                        type: 'Rectangle',
                        coordinates: [[-38, -26], [62, 22]]
                      } : {
                        type: 'Circle',
                        coordinates: [12, 12],
                        radius: 11
                      },
                      clusterIconOffset: [-12, -12],
                      zIndex: 6
                    }}
                    options={{
                      clusterize: false,
                      clusterDisableClickZoom: true
                    }}
                    features={objectManagerFeatures}
                    onClick={handleClickOnPoint}
                  />
                </Fragment>
              )}
              <Button
                options={{
                  position: width > 992 ? { top: 12, right: 72 } : { top: 12, left: 12 },
                  maxWidth: 'none',
                  selectOnClick: false
                }}
                data={{
                  content: `<div style="margin: 0 18px;display:flex;"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"><path d="M2.88,3a2.31,2.31,0,1,0,2.3,2.31A2.31,2.31,0,0,0,2.88,3Z"/><path d="M8.59,6.27H22.77a1,1,0,0,0,0-2H8.59a1,1,0,0,0,0,2Z"/><path d="M2.88,9.69A2.31,2.31,0,1,0,5.18,12,2.31,2.31,0,0,0,2.88,9.69Z"/><path d="M22.77,11H8.59a1,1,0,0,0,0,2H22.77a1,1,0,0,0,0-2Z"/><path d="M2.88,16.42a2.31,2.31,0,1,0,2.3,2.31A2.3,2.3,0,0,0,2.88,16.42Z"/><path d="M22.77,17.73H8.59a1,1,0,0,0,0,2H22.77a1,1,0,0,0,0-2Z"/></svg></div>`
                }}
                onClick={handleCatalogBackClick}
              />
              <Button
                options={{
                  position: width > 992 ? { top: 132, right: 12 } : { bottom: 32, right: 12 },
                  selectOnClick: false
                }}
                data={{
                  content: `${
                    isAnyFilters(objectsState.filter)
                      ? `<span style="position: absolute; right: 8px; bottom: 8px; background-color: #E8505B; width: 8px; height: 8px; border-radius: 50%;"></span>`
                      : ''
                  }<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"><path d="M22.63.18H1.37a1,1,0,0,0-1,1V5a1,1,0,0,0,1,1H2l7.27,9.1v7.69a1,1,0,0,0,1,1h3.44a1,1,0,0,0,1-1V15.13L22,6h.64a1,1,0,0,0,1-1V1.18A1,1,0,0,0,22.63.18Zm-9.91,14.6v7H11.28v-7a1,1,0,0,0-.22-.62L4.57,6H19.43l-6.49,8.13A1,1,0,0,0,12.72,14.78ZM2.37,4V2.18H21.63V4H2.37Z"/></svg>`
                }}
                onClick={() =>
                  setObjectsState({
                    type: 'SET_STATE',
                    payload: { ...objectsState.state, isFiltersMobileVisible: true }
                  })
                }
              />
              <GeolocationControl
                options={{
                  position: width > 992 ? { right: 12, top: 12 } : { right: 12, top: 12 }
                }}
              />
              <Button
                options={{
                  position: width > 992 ? { top: 72, right: 12 } : { bottom: 90, right: 12 }
                }}
                data={{
                  content: Boolean(drawPolygon.length) || isDrawing
                    ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
                    : '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke-width="2"><path d="M17.87,9.64a2.43,2.43,0,0,0-.61.08A2.45,2.45,0,0,0,15,8.22a2.32,2.32,0,0,0-.92.18,2.51,2.51,0,0,0-2.37-.94V4.05a2.43,2.43,0,0,0-4.86,0v8.82l-.4-.39a2.84,2.84,0,0,0-3.94,0,2.09,2.09,0,0,0,0,2.93L9,21.89a6.71,6.71,0,0,0,11.27-4.34l0-5.47A2.43,2.43,0,0,0,17.87,9.64Zm.41,7.8a4.71,4.71,0,0,1-7.89,3L3.94,14v-.11a.76.76,0,0,1,.55-.23.79.79,0,0,1,.56.23L7.15,16a1,1,0,0,0,1.09.22,1,1,0,0,0,.62-.93V4.05a.43.43,0,0,1,.86,0v8a1,1,0,0,0,2,0V9.85a.42.42,0,0,1,.43-.42.43.43,0,0,1,.43.43v.79h0V12a1,1,0,0,0,2,0V10.64a.43.43,0,0,1,.86,0V12a1,1,0,0,0,.46.84l.1.06a1,1,0,0,0,1,0,1,1,0,0,0,.48-.83.44.44,0,0,1,.43-.42.43.43,0,0,1,.43.43Z"/><path d="M22.06,5.43a6.49,6.49,0,0,0-9.61-4.25,1,1,0,0,0-.36,1.37,1,1,0,0,0,1.36.36,4.5,4.5,0,0,1,6.66,2.94,1,1,0,0,0,1,.79l.21,0A1,1,0,0,0,22.06,5.43Z"/></svg>'
                }}
                defaultState={{ selected: Boolean(drawPolygon.length) || isDrawing }}
                instanceRef={(ref) => {
                  if (ref)
                    drawButtonId.current = ref;
                }}
                onClick={handleDrawButtonClick}
              />
              <ZoomControl
                options={{
                  position: { right: '12px', top: 'calc(50vh - 104px)' },
                  size: 'small'
                }}
              />
            </Map>
          )}
        </TemplateProvider>
        {width > 992 && isMapReady && <ObjectsMapCatalog />}
        {width < 992 && isMapReady && <ObjectsMapCatalogMobile />}
      </YMaps>
    </section>
  );
};

export default ObjectsMap;
